import {Component, inject, OnInit} from '@angular/core';
import {TranslatePipe, TranslateService} from '@ngx-translate/core';
import {FormControl, ReactiveFormsModule} from '@angular/forms';
import {RouterLink} from '@angular/router';
import {finalize} from 'rxjs';
import {NzAlertModule} from 'ng-zorro-antd/alert';
import {NzButtonModule} from 'ng-zorro-antd/button';
import {NzCardModule} from 'ng-zorro-antd/card';
import {NzInputModule} from 'ng-zorro-antd/input';
import {NzPaginationModule} from 'ng-zorro-antd/pagination';
import {NzTableModule} from 'ng-zorro-antd/table';
import {NzTagModule} from 'ng-zorro-antd/tag';
import {NzModalModule, NzModalService} from 'ng-zorro-antd/modal';
import {NzDropDownModule} from 'ng-zorro-antd/dropdown';
import {NzMenuModule} from 'ng-zorro-antd/menu';
import {NzIconModule} from 'ng-zorro-antd/icon';
import {SetEmailModal, SetEmailModalData} from './set-email-modal/set-email-modal';
import {SetRolesModal, SetRolesModalData} from './set-roles-modal/set-roles-modal';
import {SetPsdModal, SetPsdModalData} from './set-psd-modal/set-psd-modal';
import {BlockUserModal} from './block-user-modal/block-user-modal';
import {BlockUserModalData} from './block-user-modal/block-user-modal-data';
import {AuthFlowService, ManageUser} from '../../core/services/auth-flow.service';
import {AuthApiService} from '../../core/services/auth-api.service';
import {AppRole} from '../../core/models/app-role';

@Component({
  selector: 'app-admin-users',
  standalone: true,
  imports: [
    TranslatePipe,
    ReactiveFormsModule,
    RouterLink,
    NzAlertModule,
    NzButtonModule,
    NzCardModule,
    NzInputModule,
    NzPaginationModule,
    NzTableModule,
    NzTagModule,
    NzModalModule,
    NzDropDownModule,
    NzMenuModule,
    NzIconModule
  ],
  templateUrl: './admin-users.html',
  styleUrl: './admin-users.css'
})
export class AdminUsers implements OnInit {
  private readonly translate = inject(TranslateService);
  private readonly authFlow = inject(AuthFlowService);
  private readonly modal = inject(NzModalService);
  private readonly authApi = inject(AuthApiService);

  readonly isSuperAdmin = this.authApi.isSuperAdmin;
  private readonly currentUser = this.authApi.user;

  readonly username = new FormControl('', {nonNullable: true});
  readonly email = new FormControl('', {nonNullable: true});

  users: ManageUser[] = [];
  page = 1;
  readonly pageSize = 10;
  totalElements = 0;
  isLoading = false;
  errorMessage = '';

  ngOnInit(): void {
    this.loadUsers();
  }

  applyFilters(): void {
    this.page = 1;
    this.loadUsers();
  }

  resetFilters(): void {
    this.username.reset();
    this.email.reset();
    this.page = 1;
    this.loadUsers();
  }

  changePage(page: number): void {
    this.page = page;
    this.loadUsers();
  }

  isLocked(user: ManageUser): boolean {
    return user.accountState === 2 || user.accountState === 'Locked';
  }

  canSetPsd(user: ManageUser): boolean {
    if (user.id.toLowerCase() === this.currentUser()?.sub?.toLowerCase()) return false;
    const roles = user.roles ?? [];
    return !roles.includes(AppRole.Admin) && !roles.includes(AppRole.SuperAdmin);
  }

  canBlock(user: ManageUser): boolean {
    if (!this.isSuperAdmin() || this.isLocked(user)) return false;
    if (user.id.toLowerCase() === this.currentUser()?.sub?.toLowerCase()) return false;
    return !(user.roles ?? []).includes(AppRole.SuperAdmin);
  }

  roleLabel(role: string): string {
    switch (role) {
      case AppRole.Admin:
        return 'adminUsers.roleAdmin';
      case AppRole.SuperAdmin:
        return 'adminUsers.roleSuperAdmin';
      default:
        return role;
    }
  }

  editEmail(user: ManageUser): void {
    const modalRef = this.modal.create<SetEmailModal, SetEmailModalData>({
      nzTitle: this.translate.instant('adminUsers.modalTitle'),
      nzContent: SetEmailModal,
      nzData: {userId: user.id, username: user.username, email: user.email},
      nzOkText: this.translate.instant('common.save'),
      nzCancelText: this.translate.instant('common.cancel'),
      nzOnOk: (instance) => {
        instance.handleOk();
        return false;
      },
    });

    modalRef.afterClose.subscribe((changed?: boolean) => {
      if (changed) this.loadUsers();
    });
  }

  editRoles(user: ManageUser): void {
    const modalRef = this.modal.create<SetRolesModal, SetRolesModalData>({
      nzTitle: this.translate.instant('adminUsers.rolesTitle'),
      nzContent: SetRolesModal,
      nzData: {userId: user.id, username: user.username, roles: user.roles ?? []},
      nzOkText: this.translate.instant('common.save'),
      nzCancelText: this.translate.instant('common.cancel'),
      nzOnOk: (instance) => {
        instance.handleOk();
        return false;
      },
    });

    modalRef.afterClose.subscribe((changed?: boolean) => {
      if (changed) this.loadUsers();
    });
  }

  editPsd(user: ManageUser): void {
    const modalRef = this.modal.create<SetPsdModal, SetPsdModalData>({
      nzTitle: this.translate.instant('adminUsers.psdTitle'),
      nzContent: SetPsdModal,
      nzData: {userId: user.id, username: user.username},
      nzOkText: this.translate.instant('common.save'),
      nzCancelText: this.translate.instant('common.cancel'),
      nzOkDisabled: true,
      nzOnOk: (instance) => {
        instance.handleOk();
        return false;
      },
    });

    modalRef.afterClose.subscribe((changed?: boolean) => {
      if (changed) this.loadUsers();
    });
  }

  block(user: ManageUser): void {
    const modalRef = this.modal.create<BlockUserModal, BlockUserModalData>({
      nzTitle: this.translate.instant('adminUsers.blockTitle'),
      nzContent: BlockUserModal,
      nzData: {userId: user.id, username: user.username},
      nzOkText: this.translate.instant('adminUsers.block'),
      nzOkDanger: true,
      nzCancelText: this.translate.instant('common.cancel'),
      nzOnOk: (instance) => {
        instance.handleOk();
        return false;
      },
    });

    modalRef.afterClose.subscribe((changed?: boolean) => {
      if (changed) this.loadUsers();
    });
  }

  unblock(user: ManageUser): void {
    this.errorMessage = '';
    this.authFlow.unblockManagedUser(user.id).subscribe({
      next: () => this.loadUsers(),
      error: () => {
        this.errorMessage = this.translate.instant('adminUsers.unblockError');
      }
    });
  }

  private loadUsers(): void {
    this.isLoading = true;
    this.errorMessage = '';

    this.authFlow.managedUsers({
      page: this.page,
      pageSize: this.pageSize,
      username: this.username.value.trim() || undefined,
      email: this.email.value.trim() || undefined
    })
      .pipe(finalize(() => this.isLoading = false))
      .subscribe({
        next: response => {
          this.users = response.list;
          this.totalElements = response.totalElements;
        },
        error: () => {
          this.users = [];
          this.totalElements = 0;
          this.errorMessage = this.translate.instant('adminUsers.loadError');
        }
      });
  }
}
